import React, { useState, useEffect, useMemo } from 'react';
import { ArrowPathIcon, ExclamationCircleIcon, LinkIcon, DocumentMagnifyingGlassIcon, QuestionMarkCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import Modal from '../../../../components/ui/Modal';
import { REPORT_LLM_MODEL, AVAILABLE_MODELS, loadReportConfig } from '../utils/reportConfig';
import { reportSettingsOf } from '../utils/reportSettings';
import { useCustomPrompts } from '../hooks/useCustomPrompts';
import { ModelOption, OptionCheckbox, PromptCard } from './ReportConfigParts';
import { splitTrimmed } from '../../../../utils/text';

const MODAL_TITLE = 'Configure Report Generation';

const preferredModel = (models, current) => {
  if (models.some((model) => model.value === current)) return current;
  return (models.find((model) => model.isDefault) || models[0]).value;
};

/**
 * Report generation with a chosen model, optional sections and custom
 * prompts. `onSubmit` receives the generation config.
 */
const ReportConfigModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialSettings = null,
  title = MODAL_TITLE,
  submitLabel = 'Generate Report',
  intro = null,
}) => {
  const [submitError, setSubmitError] = useState(null);
  const [config, setConfig] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [deeplinksEnabled, setDeeplinksEnabled] = useState(true);
  const [inDepthEnabled, setInDepthEnabled] = useState(true);
  const [verifiedReport, setVerifiedReport] = useState(false);
  const [generateQa, setGenerateQa] = useState(false);
  const [questions, setQuestions] = useState('');
  const [llmModel, setLlmModel] = useState(REPORT_LLM_MODEL);

  const editablePrompts = useMemo(() => config?.editablePrompts || [], [config?.editablePrompts]);
  const defaultPrompts = useMemo(() => config?.defaultPrompts || {}, [config?.defaultPrompts]);
  const prompts = useCustomPrompts(editablePrompts, defaultPrompts);
  const { resetAll: resetPrompts } = prompts;

  useEffect(() => {
    if (!isOpen) return;
    const settings = reportSettingsOf(initialSettings);
    resetPrompts(settings.custom_prompts || {});
    setSubmitError(null);
    setDeeplinksEnabled(settings.deeplinks_enabled);
    setInDepthEnabled(settings.in_depth_enabled);
    setVerifiedReport(settings.report_pipeline === 'v3');
    setGenerateQa(settings.generate_qa);
    setQuestions(settings.questions.join('\n'));
    setLlmModel(settings.llm_model);

    setIsLoadingConfig(true);
    setConfigError(null);
    loadReportConfig()
      .then(setConfig)
      .catch((error) => setConfigError(error.message || 'Could not load the report configuration'))
      .finally(() => setIsLoadingConfig(false));
  }, [isOpen, initialSettings, resetPrompts]);

  const availableModels = useMemo(() => {
    return config?.availableModels?.length ? config.availableModels : AVAILABLE_MODELS;
  }, [config?.availableModels]);

  useEffect(() => {
    if (!isOpen || !availableModels.length) return;
    setLlmModel(preferredModel(availableModels, reportSettingsOf(initialSettings).llm_model));
  }, [isOpen, availableModels, initialSettings]);

  const handleSubmit = async () => {
    setSubmitError(null);

    const { prompts: enabledCustomPrompts, error } = prompts.collect();
    if (error) {
      setSubmitError(error);
      return;
    }

    const questionsList = splitTrimmed(questions);
    if (generateQa && questionsList.length === 0) {
      setSubmitError('Enter at least one question when Q&A analysis is enabled.');
      return;
    }

    try {
      await onSubmit({
        llm_model: llmModel || REPORT_LLM_MODEL,
        custom_prompts: Object.keys(enabledCustomPrompts).length > 0 ? enabledCustomPrompts : null,
        deeplinks_enabled: deeplinksEnabled,
        in_depth_enabled: inDepthEnabled,
        report_pipeline: verifiedReport ? 'v3' : 'v1',
        generate_qa: generateQa && questionsList.length > 0,
        questions: questionsList,
      });
    } catch (error) {
      setSubmitError(error.message || 'Could not save these settings');
    }
  };

  if (isLoadingConfig) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={title} size="xl">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading configuration...</p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="xl">
      <div className="space-y-6 max-h-[70vh] overflow-y-auto">
        {intro && <p className="text-sm text-gray-600">{intro}</p>}
        <div className="border border-gray-200 rounded-lg p-4 space-y-4">
          <h3 className="text-sm font-semibold text-gray-900">Generation options</h3>
          <div>
            <span className="text-sm font-medium text-gray-900">Report model</span>
            <p className="text-xs text-gray-500 mt-1">
              Mistral counts input and output together inside the context window. Report generation asks for up to 50k output tokens.
            </p>
            <div className="mt-2 space-y-2">
              {availableModels.map((model) => (
                <ModelOption
                  key={model.value}
                  model={model}
                  selected={model.value === llmModel}
                  onSelect={() => setLlmModel(model.value)}
                />
              ))}
            </div>
          </div>
          <OptionCheckbox
            checked={deeplinksEnabled}
            onToggle={() => setDeeplinksEnabled((prev) => !prev)}
            Icon={LinkIcon}
            title="Include deeplinks"
          >
            Convert transcript citations into session page links when publishing to DigWatch or FAICON.
          </OptionCheckbox>
          <OptionCheckbox
            checked={inDepthEnabled}
            onToggle={() => setInDepthEnabled((prev) => !prev)}
            Icon={DocumentMagnifyingGlassIcon}
            title="Include in-depth analysis"
          >
            Generate agreements, differences, takeaways, and follow-up questions. Uncheck to skip that section.
          </OptionCheckbox>
          <OptionCheckbox
            checked={verifiedReport}
            onToggle={() => setVerifiedReport((prev) => !prev)}
            Icon={ShieldCheckIcon}
            title="Verified full report (v3)"
          >
            Write the full report speaker by speaker and check every sentence against the transcript. English sessions
            with the default model and without custom report/review/rewrite prompts; otherwise the standard report is used.
          </OptionCheckbox>
          <OptionCheckbox
            checked={generateQa}
            onToggle={() => setGenerateQa((prev) => !prev)}
            Icon={QuestionMarkCircleIcon}
            title="Q&A analysis"
          >
            Analyze custom questions against this session. Shown as an extra tab in the app;
            included when you publish to DigWatch (not sent to FAICON).
          </OptionCheckbox>
          {generateQa && (
            <label className="block">
              <span className="text-xs font-medium text-gray-700">Questions for analysis</span>
              <textarea
                value={questions}
                onChange={(e) => setQuestions(e.target.value)}
                rows={4}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter one question per line"
              />
            </label>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900">Custom Prompts</h4>
            <button
              onClick={prompts.resetAll}
              className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Reset All
            </button>
          </div>
          {configError ? (
            <p className="text-xs text-red-600">
              Prompts could not be loaded ({configError}), so they cannot be customized now. The report will use the default prompts.
            </p>
          ) : (
            <p className="text-xs text-gray-500">
              Customize prompts to change how the report is generated. Make sure to include all required placeholders.
            </p>
          )}

          <div className="space-y-2">
            {editablePrompts.map((promptConfig) => (
              <PromptCard
                key={promptConfig.key}
                promptConfig={promptConfig}
                {...prompts.cardState(promptConfig.key)}
              />
            ))}
          </div>
        </div>

        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <ExclamationCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800 whitespace-pre-line">
                {submitError}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={prompts.hasErrors}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ReportConfigModal;
